import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setConnect, setReconnecting, setReconnectionError, clearReconnectionError } from '../ReduxSlices/AuthSlice';
import { walletPersistenceService } from '../Services/walletPersistenceService';
import { toast } from 'react-toastify';

/**
 * Hook to handle wallet reconnection on app initialization
 * This hook runs when the app starts and attempts to reconnect
 * previously connected wallets if they're still available.
 */
export const useWalletReconnection = () => {
  const dispatch = useDispatch();
  const { account, wallet, isConnected, isReconnecting } = useSelector((state) => state.auth);

  useEffect(() => {
    const attemptWalletReconnection = async () => {
      // Only attempt reconnection if we have persisted wallet data
      // and we're not already connected or reconnecting
      if (!account || !wallet || isConnected || isReconnecting) {
        return;
      }

      // Check if the wallet is available
      if (!walletPersistenceService.isWalletAvailable(wallet)) {
        console.log(`Wallet ${wallet} not available for reconnection`);
        return;
      }

      console.log(`Attempting to reconnect ${wallet} wallet for account ${account}`);
      
      dispatch(setReconnecting(true));
      dispatch(clearReconnectionError());

      try {
        let result;

        switch (wallet) {
          case 'leap':
            result = await walletPersistenceService.reconnectLeapWallet(account);
            break;
          case 'keplr':
            result = await walletPersistenceService.reconnectKeplrWallet(account);
            break;
          default:
            throw new Error(`Unsupported wallet type: ${wallet}`);
        }

        if (result.success) {
          // Update Redux state with reconnected wallet
          dispatch(setConnect({
            account: result.address,
            wallet: wallet
          }));

          console.log(`Successfully reconnected ${wallet} wallet`);
          
          // Show success toast
          toast.success(`Wallet reconnected successfully!`, {
            autoClose: 2000,
          });

          // Reset reconnection attempts on success
          walletPersistenceService.resetReconnectionAttempts();
        } else {
          // Handle reconnection failure
          const errorMessage = result.error || 'Reconnection failed';
          
          dispatch(setReconnectionError(errorMessage));
          
          console.log(`Failed to reconnect ${wallet} wallet:`, errorMessage);
          
          // Show error toast only if it's not a manual reconnection requirement
          if (!result.requiresManualReconnection) {
            toast.error(`Wallet reconnection failed: ${errorMessage}`, {
              autoClose: 3000,
            });
          }
        }
      } catch (error) {
        const errorMessage = error.message || 'Unknown reconnection error';
        
        dispatch(setReconnectionError(errorMessage));
        
        console.error(`Wallet reconnection error:`, error);
        
        toast.error(`Wallet reconnection failed: ${errorMessage}`, {
          autoClose: 3000,
        });
      }
    };

    // Add a small delay to ensure the app is fully loaded
    const timeoutId = setTimeout(attemptWalletReconnection, 1000);

    return () => clearTimeout(timeoutId);
  }, [account, wallet, isConnected, isReconnecting, dispatch]);

  // Return reconnection state for components that need it
  return {
    isReconnecting,
    reconnectionError: useSelector((state) => state.auth.reconnectionError),
    canAttemptReconnection: !!(account && wallet && !isConnected && !isReconnecting)
  };
};
